import React from 'react';
import { FixedSizeList } from 'react-window';

import * as styles from './styles';
import {
  type CheckboxOption,
  DropdownComponent,
  type OptionKey,
} from './FilterMultipleDropdown';
import { FormGroup } from '@material-ui/core';

import { Button } from '@cobliteam/ui/components';

import { SearchRounded } from '@material-ui/icons';

import { Chip } from '../Chip/Chip';
import { ContainerDefault } from '../Containers';
import { Divider } from '../Divider';
import type { CloseOverlayHandler } from '../Dropdown';
import { DefaultInput } from '../Input';
import { Checkbox } from '../SelectionControls';
import { applyCustomStyles } from '../styles';
import { DescriptionTitle, PlaceholderText } from '../typography';

export interface FilterMultipleDropdownOverlayProps {
  options: CheckboxOption[];
  selectedOptionKeys: Set<OptionKey>;
  selectionChanged: boolean;
  className?: string;
  withActionButtons?: boolean;
  overlayTitle?: string;
  cleanFilterTitle?: string;
  searchBoxPlaceholder?: string;
  noOptionsPlaceholder?: string;
  renderComponent: DropdownComponent;
  onOptionSelection: (option: CheckboxOption) => void;
  onClearFilter?: () => void;
  onApplyFilter: () => void;
  onCancel: () => void;
  closeOverlayHandler: CloseOverlayHandler;
}

const CustomPaper = applyCustomStyles(ContainerDefault);

let searchDebounceRef: any = null;
const searchDebounce = (fn: () => void, wait: number) => {
  if (searchDebounceRef) clearTimeout(searchDebounceRef);
  searchDebounceRef = setTimeout(fn, wait);
};

export const FilterMultipleDropdownOverlay: React.FC<
  FilterMultipleDropdownOverlayProps
> = ({
  options,
  selectedOptionKeys,
  selectionChanged,
  overlayTitle,
  cleanFilterTitle = 'Limpar filtros',
  withActionButtons,
  searchBoxPlaceholder,
  noOptionsPlaceholder,
  className,
  renderComponent,
  ...props
}) => {
  const [searchBoxInput, setSearchBoxInput] = React.useState('');

  // if height <= 760
  const heightVirtualList = window.innerHeight <= 760 ? 120 : 195;

  const renderClearFilter = () => (
    <>
      <Button
        variant='text'
        size='condensed'
        disabled={options.length === 0 || selectedOptionKeys.size === 0}
        className='m-5 max-w-24 justify-start'
        onClick={props.onClearFilter}
      >
        {cleanFilterTitle}
      </Button>
      <Divider />
    </>
  );

  const filterOptionsBySearchBoxInput = () => {
    const normalizedSearchBoxInput = searchBoxInput.trim().toLowerCase();

    if (normalizedSearchBoxInput.length > 0) {
      return options.filter(({ label }) =>
        label.toLocaleLowerCase().match(normalizedSearchBoxInput)
      );
    }

    return [...options];
  };

  const handleSearchBoxInputChange = ({
    currentTarget: { value },
  }: React.ChangeEvent<HTMLInputElement>) => {
    searchDebounce(() => {
      setSearchBoxInput(value);
    }, 400);
  };

  const filteredOptions = filterOptionsBySearchBoxInput();

  const renderOptions = (option: any) => {
    return renderComponent === DropdownComponent.CHIP ? (
      <Chip
        key={option.key}
        active={selectedOptionKeys.has(option.key)}
        label={option.label}
        onClick={() => props.onOptionSelection(option)}
      />
    ) : (
      <Checkbox
        key={option.key}
        checked={selectedOptionKeys.has(option.key)}
        onChange={() => props.onOptionSelection(option)}
        value={option.key}
        label={option.label}
      />
    );
  };

  const renderRow = ({ index, style }: { index: number; style: any }) => {
    const option = filteredOptions[index];
    return <div style={style}>{renderOptions(option)}</div>;
  };

  const renderActionButtons = () => (
    <>
      <Divider />
      <div className='m-5 flex items-center justify-between'>
        <Button variant='text' onClick={props.onCancel}>
          Cancelar
        </Button>
        <Button
          size='condensed'
          disabled={options.length === 0 || !selectionChanged}
          onClick={props.onApplyFilter}
        >
          Aplicar
        </Button>
      </div>
    </>
  );

  return (
    <CustomPaper
      customStyles={styles.paperCustomStyle}
      className={`${styles.overlay} ${className || ''}`}
    >
      {props.onClearFilter && renderClearFilter()}

      <div className={styles.optionsContainerScroll(!!searchBoxPlaceholder)}>
        {searchBoxPlaceholder && (
          <DefaultInput
            startAdornment={<SearchRounded />}
            placeholder={searchBoxPlaceholder}
            onChange={handleSearchBoxInputChange}
            className={styles.searchBox}
          />
        )}
        {overlayTitle && (
          <DescriptionTitle className={styles.overlayTitle}>
            {overlayTitle}
          </DescriptionTitle>
        )}
        {filteredOptions.length ? (
          <div className={styles.optionsContainer}>
            <FormGroup
              row={renderComponent === DropdownComponent.CHIP}
              className={
                renderComponent === DropdownComponent.CHIP
                  ? styles.chip
                  : styles.checkbox
              }
            >
              <FixedSizeList
                className={styles.scrollBar}
                itemCount={filteredOptions.length}
                height={heightVirtualList}
                itemSize={40}
                width='306px'
              >
                {renderRow}
              </FixedSizeList>
            </FormGroup>
          </div>
        ) : (
          <div className={styles.noOptionsPlaceholder}>
            <PlaceholderText>
              {noOptionsPlaceholder || 'Nenhuma opção encontrada.'}
            </PlaceholderText>
          </div>
        )}
      </div>

      {withActionButtons && renderActionButtons()}
    </CustomPaper>
  );
};
